import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '@/lib/supabase';
import { getStockStatus, STOCK_STATUS_META, type Product } from '@/lib/types';

interface ProductRow extends Product {
  category: { id: string; name: string } | null;
}

const HEADERS = [
  'Nombre',
  'SKU',
  'Código de barras',
  'Marca',
  'Categoría',
  'Precio de compra',
  'Precio de venta',
  'Impuesto (%)',
  'Stock',
  'Stock mínimo',
  'Estado',
  'En catálogo',
  'Activo',
];

async function fetchProductsWithStock(branchId: string | null): Promise<ProductRow[]> {
  const { data, error } = await supabase
    .from('products')
    .select('*, category:categories(id, name)')
    .order('name', { ascending: true });
  if (error) throw new Error(error.message);

  const rows = (data ?? []) as ProductRow[];
  if (rows.length === 0) return rows;

  const ids = rows.map((p) => p.id);
  let stockMap = new Map<string, number>();

  if (branchId) {
    const { data: invData } = await supabase
      .from('branch_inventory')
      .select('product_id, stock')
      .eq('branch_id', branchId)
      .in('product_id', ids);
    for (const r of (invData ?? []) as { product_id: string; stock: number }[]) {
      stockMap.set(r.product_id, Number(r.stock ?? 0));
    }
  } else {
    const { data: invData } = await supabase
      .from('branch_inventory')
      .select('product_id, stock')
      .in('product_id', ids);
    const map = new Map<string, number>();
    for (const r of (invData ?? []) as { product_id: string; stock: number }[]) {
      map.set(r.product_id, (map.get(r.product_id) ?? 0) + Number(r.stock ?? 0));
    }
    stockMap = map;
  }

  return rows.map((p) => ({ ...p, stock: stockMap.get(p.id) ?? 0 }));
}

function buildRows(products: ProductRow[]): Record<string, string | number>[] {
  return products.map((p) => {
    const stock = Number(p.stock ?? 0);
    const minStock = p.min_stock != null ? Number(p.min_stock) : 0;
    const status = getStockStatus(stock, minStock);
    return {
      Nombre: p.name,
      SKU: p.sku ?? '',
      'Código de barras': p.barcode ?? '',
      Marca: p.brand ?? '',
      Categoría: p.category?.name ?? '',
      'Precio de compra': p.purchase_price != null ? Number(p.purchase_price) : 0,
      'Precio de venta': Number(p.sale_price ?? 0),
      'Impuesto (%)': p.tax_percentage != null ? Number(p.tax_percentage) : 0,
      Stock: stock,
      'Stock mínimo': minStock,
      Estado: STOCK_STATUS_META[status].label,
      'En catálogo': p.show_in_catalog ? 'Sí' : 'No',
      Activo: p.active ? 'Sí' : 'No',
    };
  });
}

function dateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

export async function exportProductsToExcel(branchId: string | null): Promise<number> {
  const products = await fetchProductsWithStock(branchId);
  const rows = buildRows(products);
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Productos');
  XLSX.writeFile(wb, `productos_${dateStamp()}.xlsx`);
  return rows.length;
}

export async function exportProductsToPdf(branchId: string | null): Promise<number> {
  const products = await fetchProductsWithStock(branchId);
  const rows = buildRows(products);

  const body = rows.map((r) =>
    HEADERS.map((h) => {
      const v = r[h];
      if (typeof v === 'number') {
        if (h.includes('Precio') || h.includes('Impuesto')) return v.toFixed(2);
        return String(v);
      }
      return String(v ?? '');
    })
  );

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFontSize(15);
  doc.text('Listado de productos', 40, 40);
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(`Generado: ${new Date().toLocaleString()} — ${rows.length} productos`, 40, 56);

  autoTable(doc, {
    head: [HEADERS],
    body,
    startY: 68,
    styles: { fontSize: 7, cellPadding: 3 },
    headStyles: { fillColor: [44, 44, 44], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  });

  doc.save(`productos_${dateStamp()}.pdf`);
  return rows.length;
}